// #1780 — the eyeball's FAIL path, shared by the single and bulk assign pages.
//
// ed, 2026-09-09: "the pass is ok, but when the eyeball fails, i want details in
// the popup with a clear button."
//
// A MIXIN rather than a copy in each page. The two pages already carried a
// character-identical `ckeckHTS`, and this adds ~90 lines around it — two copies
// of that is the shape that let #1871 fix one of two identical sites and miss the
// other, and the shape #1873 had to go back and remove. One definition, two
// hosts.
//
// The one thing it deliberately does NOT define is `onHtsAssign`, because
// "proceed anyway" is not the same action on the two pages: single assigns, bulk
// ticks the checkbox its call-count guard depends on. Each host defines it.
import service from './services.js';
import { blockerList, blockedMessage } from './htsBlockers.js';

export default {
    data() {
        return {
            // `employeeId` is held so Assign / Clear act on the worker the popup
            // is about, not on whatever has been clicked since it opened.
            htsPopup: {
                visible: false,
                loading: false,
                employeeId: null,
                blockers: [],
                details: { recurring: [], vacation: [], bookings: [] },
                windowRange: null,
            },
        };
    },
    methods: {
        htsHeaders() {
            return {
                'Authorization': this.userToken,
                'cs3-context-role': this.userRole,
                'cs3-context-clientId': this.userClientID,
                'jobId': atob(this.$route.params.id),
            };
        },

        ckeckHTS(emp_id) {
            service.get('employee/checkEmployeeAvailability',
                Object.assign(this.htsHeaders(), { 'employeeId': emp_id }))
                .then((res) => {
                    const result = (res && res.data && res.data.result) || {};

                    if (result.status) {
                        // "the pass is ok" (ed). Unchanged, 4.5 s default.
                        this.$notify({ title: 'Success', message: result.message, type: 'success' });
                        return;
                    }

                    // THE FAIL PATH. Name the blockers; hold the message for 8 s
                    // rather than Element UI's 4.5 s default, which is tuned for
                    // "Saved successfully" and not for a judgement about whether
                    // someone can work; and open the review popup.
                    const blockers = blockerList(result.blockers);
                    this.$notify({
                        title: 'Error',
                        message: blockedMessage(result.message, blockers),
                        type: 'error',
                        duration: 8000,
                        onClick: () => { this.openHtsDetails(emp_id, blockers); },
                    });
                    this.openHtsDetails(emp_id, blockers);
                })
                .catch((err) => {
                    console.log(err);
                });
        },

        /**
         * Detail for ONE worker, fetched when the popup opens (~13 ms). Never for
         * a list: `forUsers` exists (#1656) because the per-user path in a loop
         * took prod-29 to 419 s, and the list already has what it needs.
         */
        openHtsDetails(emp_id, blockers) {
            this.htsPopup.employeeId = emp_id;
            if (blockers) this.htsPopup.blockers = blockers;
            this.htsPopup.details = { recurring: [], vacation: [], bookings: [] };
            this.htsPopup.windowRange = null;
            this.htsPopup.visible = true;
            this.htsPopup.loading = true;

            service.get('employee/htsBlockingDetails',
                Object.assign(this.htsHeaders(), { 'employeeId': emp_id }))
                .then((res) => {
                    this.htsPopup.loading = false;
                    const d = (res && res.data && res.data.result) || {};
                    this.htsPopup.details = {
                        recurring: d.recurring || [],
                        vacation: d.vacation || [],
                        bookings: d.bookings || [],
                    };
                    this.htsPopup.windowRange = d.window || null;
                })
                .catch((err) => {
                    console.log(err);
                    this.htsPopup.loading = false;
                    // Left open, the popup would read "None recorded" in every
                    // section — a statement about the worker's data that we have
                    // not established. Close it and say what actually happened.
                    this.closeHtsDetails();
                    this.$notify({
                        title: 'Error',
                        message: 'Could not load the blocking details. Please try again.',
                        type: 'error',
                        duration: 8000,
                    });
                });
        },

        closeHtsDetails() {
            this.htsPopup.visible = false;
            this.htsPopup.loading = false;
        },
    },
};
